import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Skull, CalendarDays } from 'lucide-react';
import { bagContents, levelRange } from '../../utils/bagSources';
import type { BagSource } from '../../utils/bagSources';
import { eventPath, itemPath, monsterName, monsterPath } from '../../utils/worldFormat';
import { DataStatus } from './DataStatus';
import { EntityThumb } from './EntityThumb';
import type { Bag, WorldData } from '../../types/world';

/** "Dónde conseguirlo" → cajas y bolsas. Una bolsa es una lista de resultados posibles, no un paquete. */
export function BagSourceList({
  w,
  sources
}: {
  w: WorldData;
  sources: BagSource[];
}) {
  return <ul className="grid gap-2.5" data-id="element-1162" data-mp="2iv1adx4dmkdm">
      {sources.map(s => <li key={`${s.bag.id}-${s.levelMin ?? ''}`} className="rounded-xl md:rounded-2xl border border-white/10 bg-[#0A1628] p-3 md:p-4 text-[13px] md:text-base" data-id="element-1163" data-mp="2iuhtmjqgq216">
          {s.origins.map(o => <p key={o.kind === 'open' ? o.itemKey : o.kind === 'monster' ? `m${o.monsterId}` : o.eventId} className="flex flex-wrap items-center gap-1.5 text-gray-200" data-id="element-1164" data-mp="17eb1g68kjegb">
              {o.kind === 'open' && <>
                  <EntityThumb entity={{
            type: 'item',
            id: o.itemKey
          }} name={o.itemName} fallback={<Package className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-1166" data-mp="1ows88xndx38l" />} data-id="element-1165" data-mp="1oupjsi1vri5z" />
                  Al abrir
                  <Link to={itemPath(o.itemKey)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-1167" data-mp="1sy5ayyv6kqba">{o.itemName}</Link>
                </>}
              {o.kind === 'monster' && <>
                  <EntityThumb entity={{
            type: 'monster',
            id: o.monsterId
          }} name={monsterName(w, o.monsterId)} fallback={<Skull className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-1169" data-mp="1a6kv8xg5ccfa" />} data-id="element-1168" data-mp="15819ax1jz0mx" />
                  Recompensa al derrotar a
                  <Link to={monsterPath(o.monsterId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-1170" data-mp="w84qvd5ray0b">{monsterName(w, o.monsterId)}</Link>
                </>}
              {o.kind === 'event' && <>
                  <CalendarDays className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-1171" data-mp="2y20xwmcvxgjl" />
                  Premio del evento <Link to={eventPath(o.eventId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-1172" data-mp="2t4qyqezp1qik">{o.eventName}</Link>
                </>}
            </p>)}
          <p className="mt-1.5 text-sm text-gray-400" data-id="element-1173" data-mp="3lqsj2ts24ymv">
            {s.giveAll ? 'Se entrega junto con el resto del contenido.' : s.poolSize ? `Uno de ${s.poolSize} resultados posibles.` : 'Uno de los resultados posibles.'}
            {levelRange(s.levelMin, s.levelMax) && ` Nivel del ítem: ${levelRange(s.levelMin, s.levelMax)}.`}
          </p>
          <p className="mt-1 text-xs text-gray-500" data-id="element-1174" data-mp="21dl3ocytyj05">Según la configuración exportada: no es un resultado garantizado.</p>
        </li>)}
    </ul>;
}

/** Ficha de una caja: qué puede salir al abrirla. */
export function BoxContents({
  w,
  bags,
  title = 'Qué puede salir al abrirla',
  intro = 'Cada apertura entrega uno de estos resultados posibles, no todos.',
  id = 'contenido'
}: {
  w: WorldData;
  bags: Bag[];
  title?: string;
  intro?: string;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const raw = bags.flatMap(b => bagContents(w, b.id));
  // Resultados retenidos por conflicto entre fuentes: no se presentan como confirmados.
  const all = raw.filter(c => !c.withheld);
  const held = raw.length - all.length;
  if (!raw.length) return null;
  const shown = open ? all : all.slice(0, 12);
  return <section id={id} aria-labelledby={`${id}-t`} className="scroll-mt-20 md:scroll-mt-32" data-id="element-1175" data-mp="14lmvk43vn5t9">
      <h2 id={`${id}-t`} className="mb-2.5 md:mb-4 flex items-baseline gap-2 font-cinzel text-lg md:text-2xl font-bold text-white" data-id="element-1176" data-mp="18lnikd51kpbx">
        {title} <span className="font-sans text-sm font-normal text-gray-500" data-id="element-1177" data-mp="3adjrrg3qln3j">{all.length}</span>
      </h2>
      <p className="mb-3 text-sm text-gray-400" data-id="element-1178" data-mp="2f3d6x3amvcxf">{intro}</p>
      {held > 0 && <p className="mb-3 flex flex-wrap items-center gap-2 text-sm text-gray-300" data-id="element-1179" data-mp="2f5hm4hdzfyd0">
          <DataStatus kind="conflict" data-id="element-1180" data-mp="2b44fi532fr51" /> {held} {held === 1 ? 'resultado de la exportación está' : 'resultados de la exportación están'} en revisión por el staff y no se muestran.
        </p>}
      <ul className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1181" data-mp="2k0n5p9jwx0xx">
        {shown.map(c => <li key={`${c.itemKey}-${c.levelMin ?? ''}`} className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#0A1628] pl-1.5 pr-3 py-1.5 transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-1182" data-mp="1wdf3x1lp0a0n">
            {/* Imagen fuera del enlace: tocarla no abre la ficha; el nombre sí. */}
            <EntityThumb entity={{
          type: 'item',
          id: c.itemKey
        }} name={c.name} data-id="element-1183" data-mp="21a5fpcqgsjg9" />
            <Link to={itemPath(c.itemKey)} className="flex min-w-0 flex-1 items-center justify-between gap-2 py-1 text-sm" data-id="element-1184" data-mp="1dl31yfyybar1">
              <span className="truncate text-white" data-id="element-1185" data-mp="18lqepu4641nx">{c.name}</span>
              {levelRange(c.levelMin, c.levelMax) && <span className="shrink-0 text-xs text-gray-400" data-id="element-1186" data-mp="3k93b9v4ehd52">{levelRange(c.levelMin, c.levelMax)}</span>}
            </Link>
          </li>)}
      </ul>
      {all.length > 12 && <button type="button" onClick={() => setOpen(v => !v)} className="mt-2 h-10 text-sm font-semibold text-[#D4AF37] hover:text-[#F3E5AB]" data-id="element-1187" data-mp="3b858th9m1dol">
          {open ? 'Mostrar menos' : `Ver los ${all.length} resultados`}
        </button>}
    </section>;
}